import React, { useEffect, useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  Row,
  Col,
  Table,
} from "reactstrap";
import {
  getProducts,
  getTasksby,
  postTask,
  patchOrder,
} from "helpers/api_helper";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";

const OrderDetailsModal = ({ isOpen, toggle, order, onRefreshOrders }) => {
  const { t } = useTranslation();
  const [modalProductStocks, setModalProductStocks] = useState([]);
  const [orderTasks, setOrderTasks] = useState([]);
  const [taskInfo, setTaskInfo] = useState("");

  useEffect(() => {
    if (!order) {
      setModalProductStocks([]);
      setOrderTasks([]);
      setTaskInfo("");
      return;
    }

    const fetchData = async () => {
      try {
        const products = JSON.parse(order.products || "[]");
        const enriched = await Promise.all(
          products.map(async (prod) => {
            const stock = (await getProducts(prod.id))[0]?.stock || 0;
            const diff = stock - parseFloat(prod.quantity);
            return { ...prod, stock, diff };
          })
        );
        setModalProductStocks(enriched);

        const tasks = await getTasksby(order.id);
        setOrderTasks(tasks);

        setTaskInfo("");
      } catch (err) {
        console.error("Error loading order details:", err);
      }
    };

    fetchData();
  }, [order]);

  const tasksByProduct = (productId) =>
    orderTasks.filter((task) => task.productid === productId);

  const readyForPackaging =
    modalProductStocks.length > 0 &&
    modalProductStocks.every((p) => p.diff >= 0) &&
    (orderTasks.length === 0 || orderTasks.every((t) => t.status === "FINISHED"));

  // ✅ show top "Create Tasks" only if PENDING-STOCK + no tasks + shortage exists
  const shortages = modalProductStocks.filter((p) => p.diff < 0);
  const canCreateShortageTasks =
    order?.status === "PENDING-STOCK" && orderTasks.length === 0 && shortages.length > 0;

  const createTasksForShortages = async () => {
    try {
      setTaskInfo(t("Creating tasks for shortages..."));

      for (const prod of shortages) {
        const deficitQty = Math.abs(prod.quantity);
        const requirements = JSON.parse(prod.requirements || "[]");
        for (const step of requirements) {
          const payload = {
            resource: [
              {
                type: step.typid,
                materials: JSON.stringify(step.materials),
                customer_name: order.name,
                status: "PENDING",
                orderref: order.id,
                productid: prod.id,
                name: prod.name,
                quantity: deficitQty,
                expectedDate: order.expected_delivery_date,
                customer_id_ref: order.customer_id || null,
                seq: step.seq,
              },
            ],
          };
          await postTask(payload);
        }
      }

      const updatedTasks = await getTasksby(order.id);
      setOrderTasks(updatedTasks);

      if (onRefreshOrders) {
        await onRefreshOrders();
      }

      setTaskInfo(t("Production tasks created."));
    } catch (err) {
      console.error("Error creating shortage tasks:", err);
      setTaskInfo(t("Error creating tasks."));
    }
  };

  const sendForPackaging = async () => {
    try {
      const payload = {
        resource: [{ id: order.id, status: "TO-PACKAGE" }],
      };
      await patchOrder(payload);

      setTaskInfo(t("Order sent for packaging."));

      if (onRefreshOrders) {
        await onRefreshOrders();
      }

      toggle();
    } catch (err) {
      console.error("Failed to update order:", err);
      setTaskInfo(t("Error sending order for packaging."));
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" className="modal-bottom" fade={false}>
      <ModalHeader toggle={toggle}>
        {t("Order Details")} - {order?.id}
      </ModalHeader>
      <ModalBody>
        {order && (
          <>
            <Row className="mb-2">
              <Col md="6">
                <strong>{t("Customer")}:</strong> {order.name}
              </Col>
              <Col md="6">
                <strong>{t("Status")}:</strong> {order.status}
              </Col>
            </Row>

            {/* 🔵 New "Create Tasks for Shortages" button */}
            {canCreateShortageTasks && (
              <div className="text-center mb-3">
                <button className="btn btn-primary btn-lg" onClick={createTasksForShortages}>
                  {t("Create Tasks for Shortages")}
                </button>
              </div>
            )}

            {/* 🟢 Existing packaging button */}
            {readyForPackaging && (
              <div className="text-center mb-3">
                <button className="btn btn-success btn-lg" onClick={sendForPackaging}>
                  {t("Send for Packaging")}
                </button>
              </div>
            )}

            <Row className="mb-2">
              <Col md="12">
                <strong>{t("Address")}:</strong> {order.delivery_address}
              </Col>
            </Row>

            <h6>{t("Order Products")}:</h6>
            <div style={{ maxHeight: 400, overflowY: "auto" }}>
              <Table bordered hover size="sm">
                <thead>
                  <tr>
                    <th>{t("Product ID")}</th>
                    <th>{t("Name")}</th>
                    <th>{t("Qty")}</th>
                    <th>{t("Stock")}</th>
                    <th>{t("Diff")}</th>
                  </tr>
                </thead>
                <tbody>
                  {modalProductStocks.map((prod, idx) => {
                    const tasks = tasksByProduct(prod.id);
                    return (
                      <React.Fragment key={idx}>
                        <tr style={{ background: "#f9f9f9" }}>
                          <td>{prod.id}</td>
                          <td>{prod.name}</td>
                          <td>{prod.quantity}</td>
                          <td>{prod.stock}</td>
                          <td style={{ color: prod.diff < 0 ? "red" : "inherit" }}>
                            {prod.diff}
                          </td>
                        </tr>
                        {tasks.map((task) => (
                          <tr key={task.id}>
                            <td colSpan={2}></td>
                            <td colSpan={3}>
                              <strong>{t("Task")} {task.id}</strong> — {t("Status")}:{" "}
                              <span className={`badge ${task.status === "FINISHED" ? "bg-success" : "bg-warning text-dark"}`}>
                                {task.status}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </Table>
            </div>

            {orderTasks.length > 0 && (
              <>
                <hr />
                <h6>{t("All Order Tasks in Production")}</h6>
                <div style={{ maxHeight: 300, overflowY: "auto" }}>
                  <Table bordered hover size="sm">
                    <thead>
                      <tr>
                        <th>{t("Task ID")}</th>
                        <th>{t("Status")}</th>
                        <th>{t("Section")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orderTasks.map((task) => (
                        <tr key={task.id}>
                          <td>{task.id}</td>
                          <td>
                            <span className={`badge ${task.status === "FINISHED" ? "bg-success" : "bg-warning text-dark"}`}>
                              {task.status}
                            </span>
                          </td>
                          <td>{task.type}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </>
            )}

            {taskInfo && <div className="alert alert-info mt-2 py-1 px-2">{taskInfo}</div>}
          </>
        )}
      </ModalBody>
    </Modal>
  );
};

OrderDetailsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
  onRefreshOrders: PropTypes.func,
  order: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    status: PropTypes.string,
    delivery_address: PropTypes.string,
    expected_delivery_date: PropTypes.string,
    customer_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    products: PropTypes.string,
  }),
};

export default OrderDetailsModal;
